import { colorCategories, stats } from "@/mocks/colors";

export default function Hero() {
  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-0 overflow-hidden bg-background-50">
      {/* 顶部细金色基准线 */}
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-accent-500/70 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-14">
          {/* 文案 */}
          <div className="lg:w-[46%]">
            <div className="flex items-center gap-3 text-accent-700 text-xs tracking-display uppercase">
              <span className="inline-block w-8 h-px bg-accent-500" />
              HLC · 黑料色图鉴
            </div>
            <h1 className="mt-5 font-serif text-foreground-950 text-[34px] leading-[1.25] md:text-5xl md:leading-[1.2] font-semibold">
              把每一档黑，
              <br />
              都认真地归好类
            </h1>
            <p className="mt-6 text-[15px] leading-7 text-foreground-600 max-w-xl">
              黑料色图鉴是一份面向设计师与配色从业者的黑色系分类归档。我们将黑料色按
              <strong className="text-foreground-900 font-medium">色系、色号与图鉴</strong>
              三种维度整理，并提供
              <strong className="text-foreground-900 font-medium">色卡与 RGB / CMYK 色号对照</strong>。
              找对应色系，直接翻目录；要可复现的参数，这里替你排好。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#atlas"
                className="whitespace-nowrap rounded-md bg-primary-900 text-background-50 px-6 py-3 text-[15px] font-medium hover:bg-primary-800 transition-colors"
              >
                进入色卡图鉴
                <i className="ri-arrow-right-line ml-1.5" />
              </a>
              <a
                href="#series"
                className="whitespace-nowrap rounded-md border border-background-300 text-foreground-900 bg-background-50 px-6 py-3 text-[15px] font-medium hover:border-primary-800 hover:text-primary-800 transition-colors"
              >
                先看色系分类
              </a>
            </div>
          </div>

          {/* 视觉色板 */}
          <div className="lg:w-[54%] grid grid-cols-5 lg:grid-cols-9 gap-2 lg:gap-2.5 items-stretch">
            <div className="col-span-2 lg:col-span-3 row-span-2 rounded-lg overflow-hidden relative min-h-[280px] md:min-h-[360px] bg-[#0a0a0b]">
              <div className="absolute inset-0 bg-gradient-to-tr from-black to-[#202024]" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-background-50">
                <div>
                  <p className="font-number text-[11px] tracking-[0.2em] opacity-70">HLC-0101</p>
                  <p className="font-serif text-lg mt-1">玄墨</p>
                </div>
                <span className="text-[11px] px-2 py-1 rounded bg-background-50/15 border border-background-50/20">高光</span>
              </div>
            </div>
            {[
              { c: "#101014", n: "曜黑", code: "0201", tag: "冷调" },
              { c: "#28282c", n: "石墨", code: "0301", tag: "哑光" },
              { c: "#8a8a8f", n: "浅烟", code: "0401", tag: "浅灰" },
              { c: "#1b1e26", n: "玄青", code: "0501", tag: "冷墨" },
              { c: "#241c17", n: "焦糖黑", code: "0602", tag: "暖调" },
            ].map((s) => (
              <div
                key={s.code}
                className="col-span-3 lg:col-span-3 rounded-lg overflow-hidden relative min-h-[132px] md:min-h-[174px]"
                style={{ backgroundColor: s.c }}
              >
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2.5 text-background-50">
                  <p className="font-number text-[10px] tracking-[0.15em] opacity-70">HLC-{s.code}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-[13px] font-medium">{s.n}</p>
                    <span className="text-[10px] opacity-70">{s.tag}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 色系光谱带 */}
      <div className="mt-12 md:mt-16">
        <div className="grid grid-cols-3 sm:grid-cols-6">
          {colorCategories.map((c) => (
            <div key={c.id} className="relative h-16 md:h-20 group cursor-pointer" style={{ backgroundColor: c.sample }}>
              <span className="absolute inset-x-0 bottom-0 h-full bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="absolute bottom-2 left-3 text-background-50 text-[11px] tracking-wider opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {c.seq} {c.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 数据条 */}
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-4">
              <span className="font-number text-4xl md:text-5xl font-semibold text-primary-900 leading-none">
                {s.num}
                <span className="text-accent-500 text-xl md:text-2xl">{s.unit}</span>
              </span>
              <div className="border-l border-background-300 pl-4">
                <p className="text-foreground-900 font-medium text-sm">{s.label}</p>
                <p className="text-foreground-500 text-xs mt-1">{s.unit}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}